// Client name mappings from abbreviated to full names
export const clientNameMappings = {
    // BOM
    'ABPLBS':'Anthem Biosciences Private Limited',
    'BANDHAN':'Bandhan Life',
    'BARC':'Broadcast Audience Research Council',
    'EML':'Emami Limited',
    'EOP':'Embassy Office Parks  Management Services Private Limited',
    'EVENTUS':'Eventus',
    'GED':'Gedu services Pvt Ltd',
    'IHP':'Indira IVF Hospital Private Limited',
    'ILI':'IndiaFirst Life Insurance Company Ltd',
    'IPL':'Intas Pharmaceutical Ltd',
    'IZM':'Infoedge',
    'JRF':'Jai Research Foundation',
    'KES':'Kanpur Electricity Supply Company Ltd',
    'KLR':'Kerala Land Record',
    'LIC':'LIC Mutual Funds',
    'LYRANET':'Lyra Network',
    'MERINO':'Merino Industries Limited',
    'MGM':'MG Motors',
    'PFC':'Power Finance Corporation Limited',
    'PERCEPTIVE':'Perceptive',
    'NAPROD':'Naprod',
    'PRIMEFOCUS':'Prime Focus Technologies Ltd',
    'PURAVANKARA':'Puravankara Limited',
    'SIDBI':'Small Industries Development Bank Of India',
    'SRI':'Sri Ramachandra Institute of Higher Education and Research',
    'TML':'TATA Metaliks Ltd',
    'UPL':'United Phosphorus Limited',
    'YES':'Yes Bank',
    'YSS':'Yes Securities',
    //AUH
    'ALF': 'Al Fakher',
    'KER':'Kerzner',
    'UPN':'Unified Payment Service Limited',
    'UUW':'Umngeni Uthukela Water',
    //JNB
    'HNC': 'Hirt & Carter',
    //AUH
    'BAE':'Bank Al Etihad',
    'QPM':'Qatar Primary Materials Company',
    'QUMC':'Qassim University Medical Centre',
    'SGH':'Saudi German Hospital',
    //SNG
    'ASDP':'ASDP Indonesia Ferry',
    'BPH':'Buddhachinaraj Phitsanulok hospital',
    'DMS':'Department of Medical Sevices',
    'GSB':'Government Savings Bank',
    'GSC':'Greenspot Co. Ltd',
    'ITH':'Information and Comm. Tech. Center at Office',
    'KPH':'Kamphaeng Phet hospital',
    'KKH':'Khon kaen hospital',
    'KTC':'Krung thai Bank',
    'LAH':'Lampang Hospital',
    'MNH':'Maharat Nakhon Ratchasima Hospital',
    'REC':'Regal Cinemas',
    'SAH':'Saraburi hospital',
    'UTH':'Udon Thani Hospital',
    'WCH':'Warinchambrab Hospital',
    'SFG':'Straits Financial Group',
    'PCSO':'Philippine Charity Sweepstakes Office',
    'DPA':'Deposit Protection Agency',
    'PORTER':'Porter',
    

};

/** Internal / test client_name values excluded from lists and counts (case-insensitive). */
const EXCLUDED_INTERNAL_CLIENT_IDS_LOWER = new Set([
  'default_other',
  'testing',
  'daily_backup_script',
]);

export const isExcludedInternalClient = (clientName) => {
  if (!clientName || typeof clientName !== 'string') return false;
  return EXCLUDED_INTERNAL_CLIENT_IDS_LOWER.has(clientName.trim().toLowerCase());
};

// Function to get full client name
export const getFullClientName = (abbreviatedName) => {
    return clientNameMappings[abbreviatedName] || abbreviatedName;
};

// Function to get abbreviated client name
export const getAbbreviatedName = (fullName) => {
    const entries = Object.entries(clientNameMappings);
    const found = entries.find(([abbr, full]) => full === fullName);
    return found ? found[0] : fullName;
};

/**
 * Stable key for the same logical client when the DB has multiple spellings
 * (e.g. BANDHAN vs Bandhan Life, CAPILLARYTECH vs CapillaryTech, LYRANET vs LyraNet).
 */
export const getCanonicalClientKey = (clientName) => {
    if (!clientName || typeof clientName !== 'string') return '';
    const t = clientName.trim();
    if (!t) return '';
    const u = t.toUpperCase();
    if (clientNameMappings[u]) return u;
    const fromFull = getAbbreviatedName(t);
    if (fromFull !== t) {
        const fu = fromFull.toUpperCase();
        if (clientNameMappings[fu]) return fu;
    }
    return u;
};

/** Same parent-id list as Clients.js / Dashboard (DICT-style families). */
export const DEFAULT_PARENT_PATTERNS = ['DICT', 'IFFCO', 'ABP', 'SF', 'XL'];

/**
 * Resolve parent id for a client using the same rules as Clients.js:
 * known parent patterns first, then prefix match against the full client list.
 */
export const getParentClientNameFromList = (clientName, clients, parentPatterns = DEFAULT_PARENT_PATTERNS) => {
    if (!clientName || typeof clientName !== 'string') return clientName;
    if (!clients || !clients.length) return clientName;

    for (const pattern of parentPatterns) {
        if (clientName !== pattern && clientName.toLowerCase().startsWith(pattern.toLowerCase())) {
            return pattern;
        }
    }

    for (const potentialParent of clients) {
        if (
            !potentialParent ||
            typeof potentialParent !== 'string' ||
            potentialParent === clientName ||
            parentPatterns.includes(potentialParent)
        ) {
            continue;
        }
        if (clientName.toLowerCase().startsWith(potentialParent.toLowerCase())) {
            return potentialParent;
        }
    }

    return clientName;
};

/**
 * Hard-coded exclusions aligned with Clients.js search filter (system / demo clients).
 */
export const isExcludedDashboardClientName = (clientName) => {
    if (!clientName || typeof clientName !== 'string') return true;
    if (isExcludedInternalClient(clientName)) return true;
    const c = clientName;
    return (
        c.toLowerCase().includes('listener') ||
        c === 'EXTRA' ||
        c === 'log_size_monitor' ||
        c === 'rsyslog_size_history' ||
        c === 'disk_usage_monitor' ||
        c === 'Advisory' ||
        c === 'rsyslog1' ||
        c === 'EventusBOM' ||
        c === 'eventus_demo' ||
        c === 'errorlogs'
    );
};

/**
 * Default Clients table view: parent-only rows + one row per canonical customer.
 * Matches Clients.js when showParentClientsOnly is true and search is empty.
 */
export const countClientsParentViewDeduped = (
    clients,
    clientLicenses,
    clientLogs,
    parentPatterns = DEFAULT_PARENT_PATTERNS,
) => {
    if (!clients || !clients.length) return 0;

    const searchFiltered = clients.filter((name) => !isExcludedDashboardClientName(name));

    const childClientsList = new Set();
    clients.forEach((clientName) => {
        const parentName = getParentClientNameFromList(clientName, clients, parentPatterns);
        if (parentName !== clientName) childClientsList.add(clientName);
    });

    const parentsList = [];
    parentPatterns.forEach((pattern) => {
        const hasChildren = clients.some(
            (clientName) =>
                clientName !== pattern &&
                clientName.toLowerCase().startsWith(pattern.toLowerCase()),
        );
        if (hasChildren) {
            parentsList.push(pattern);
        }
    });

    searchFiltered.forEach((clientName) => {
        if (parentsList.includes(clientName)) return;
        if (childClientsList.has(clientName)) return;
        parentsList.push(clientName);
    });

    return dedupeClientsByCanonicalKey(parentsList, clientLicenses || {}, clientLogs || {}).length;
};

/**
 * When /api/clients is not loaded yet: derive count from /api/client_product_count keys
 * using the same parent + canonical rules (client list = all names seen in the payload).
 */
export const countFromClientProductCountRegions = (
    clientCount,
    selectedRegion,
    clientLicenses,
    clientLogs,
    parentPatterns = DEFAULT_PARENT_PATTERNS,
) => {
    if (!clientCount || !clientCount.regions) return 0;

    const namesSet = new Set();
    if (selectedRegion && clientCount.regions[selectedRegion]?.clients) {
        Object.keys(clientCount.regions[selectedRegion].clients).forEach((n) => namesSet.add(n));
    } else {
        Object.values(clientCount.regions).forEach((regionData) => {
            if (regionData && regionData.clients) {
                Object.keys(regionData.clients).forEach((n) => namesSet.add(n));
            }
        });
    }

    const allNames = Array.from(namesSet).filter((n) => !isExcludedDashboardClientName(n));
    const uniqueParents = new Set();

    allNames.forEach((clientName) => {
        const parentName = getParentClientNameFromList(clientName, Array.from(namesSet), parentPatterns);
        if (isExcludedDashboardClientName(parentName)) return;
        uniqueParents.add(parentName);
    });

    return dedupeClientsByCanonicalKey(
        Array.from(uniqueParents),
        clientLicenses || {},
        clientLogs || {},
    ).length;
};

const licensePreferenceScore = (id, clientLicenses) => {
    const L = clientLicenses && clientLicenses[id];
    if (!L) {
        return clientNameMappings[id?.toUpperCase?.()] ? 1 : 0;
    }
    let s = 2;
    if (L.end_date || L.endDate) s += 8;
    if (L.license_per_day != null || L.licensePerDay != null) s += 4;
    if (L.engagement_type) s += 2;
    if (L.license_data) s += 1;
    if (clientNameMappings[id.toUpperCase()]) s += 3;
    return s;
};

const logSizeForClient = (id, clientLogs) => {
    if (!id || !clientLogs?.by_client?.length) return -1;
    const key = getCanonicalClientKey(id);
    let sum = 0;
    let found = false;
    for (const c of clientLogs.by_client) {
        if (getCanonicalClientKey(c.client_name) === key) {
            found = true;
            sum += Number(c.size_gb) || 0;
        }
    }
    return found ? sum : -1;
};

/** Pick one client id to represent duplicates with the same canonical key. */
export const pickPreferredClientIdForCanonical = (a, b, clientLicenses, clientLogs) => {
    const sa = licensePreferenceScore(a, clientLicenses);
    const sb = licensePreferenceScore(b, clientLicenses);
    if (sa !== sb) return sa >= sb ? a : b;
    const la = logSizeForClient(a, clientLogs);
    const lb = logSizeForClient(b, clientLogs);
    if (la !== lb) return la >= lb ? a : b;
    return a.localeCompare(b, undefined, { sensitivity: 'base' }) <= 0 ? a : b;
};

/** Deduplicate client ids that map to the same canonical customer. */
export const dedupeClientsByCanonicalKey = (clientNames, clientLicenses, clientLogs) => {
    if (!clientNames || !clientNames.length) return [];
    const buckets = new Map();
    for (const name of clientNames) {
        const key = getCanonicalClientKey(name);
        if (!key) continue;
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key).push(name);
    }
    const out = [];
    for (const variants of buckets.values()) {
        if (variants.length === 1) {
            out.push(variants[0]);
        } else {
            let best = variants[0];
            for (let i = 1; i < variants.length; i++) {
                best = pickPreferredClientIdForCanonical(best, variants[i], clientLicenses, clientLogs);
            }
            out.push(best);
        }
    }
    return out;
};

// Define Blue Ocean clients
export const BLUE_OCEAN_CLIENTS = ['CAPILLARYTECH', 'PORTER', 'IZM',"PVR","INFOEDGE"]; 